import { useCallback, useEffect } from 'react';
import { useNavigate, useBeforeUnload } from 'react-router-dom';

export function useUnsavedChangesPrompt(hasUnsavedChanges: boolean) {
    const navigate = useNavigate();

    // Handle browser back/forward/refresh
    useBeforeUnload(
        useCallback(
            (event) => {
                if (hasUnsavedChanges) {
                    event.preventDefault();
                    return (event.returnValue = 'You have unsaved changes. Are you sure you want to leave?');
                }
            },
            [hasUnsavedChanges]
        )
    );

    // Handle in-app navigation
    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        const handlePopState = (e: PopStateEvent) => {
            if (hasUnsavedChanges) {
                const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
                if (!confirmLeave) {
                    e.preventDefault();
                    window.history.pushState(null, '', window.location.href);
                }
            }
        };

        // Handle clicks on anchor tags
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');

            if (anchor &&
                anchor.href &&
                !anchor.href.startsWith('javascript:') &&
                !anchor.href.startsWith('#') &&
                anchor.target !== '_blank' &&
                !anchor.hasAttribute('download')
            ) {
                if (hasUnsavedChanges) {
                    const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
                    if (!confirmLeave) {
                        e.preventDefault();
                    }
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);
        document.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            document.removeEventListener('click', handleClick);
        };
    }, [hasUnsavedChanges, navigate]);
} 